import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { fetchDbOverview, type DbTable } from '@/lib/dbAdmin';
import { EDGE_FUNCTIONS } from '@/lib/edgeFunctions';
import ExportPanel from './components/ExportPanel';
import FunctionsPanel from './components/FunctionsPanel';
import TableExplorer from './components/TableExplorer';

export default function SuperAdminDatabasePage() {
  const { user } = useAuth();
  const [tables, setTables] = useState<DbTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchDbOverview({ userId: user.id, email: user.email });
      setTables(res.tables || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger la base de données.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const totalRows = tables.reduce((sum, t) => sum + (t.row_count || 0), 0);
  const identity = user ? { userId: user.id, email: user.email } : null;

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Base de données &amp; API</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Consultation et export de l&apos;intégralité de la base et des Edge Functions du back-end.
        </p>
      </div>

      {/* Bandeau lecture seule */}
      <div className="mb-5 flex items-start gap-3 p-4 bg-secondary-50 border border-secondary-200/70 rounded-lg">
        <i className="ri-lock-2-line text-secondary-700 mt-0.5"></i>
        <div>
          <h4 className="text-sm font-semibold text-foreground-900">Mode lecture seule</h4>
          <p className="text-xs text-foreground-600 leading-relaxed mt-0.5">
            Vous pouvez consulter et télécharger la base et ses fonctions, mais aucune suppression ni modification
            n&apos;est possible depuis cet écran. Utile pour migrer ou sauvegarder l&apos;ensemble ailleurs.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-5 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500 mb-2 block"></i>
          <p className="text-sm text-red-700 mb-3">{error}</p>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-600 text-white text-sm font-medium whitespace-nowrap hover:bg-red-700 transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            Réessayer
          </button>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <div className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center mb-2">
                <i className="ri-table-line"></i>
              </div>
              <div className="text-2xl font-bold font-heading text-foreground-950">{tables.length}</div>
              <div className="text-xs text-foreground-500">Tables</div>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <div className="w-9 h-9 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center mb-2">
                <i className="ri-terminal-box-line"></i>
              </div>
              <div className="text-2xl font-bold font-heading text-foreground-950">{EDGE_FUNCTIONS.length}</div>
              <div className="text-xs text-foreground-500">Edge Functions</div>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <div className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center mb-2">
                <i className="ri-list-check-3"></i>
              </div>
              <div className="text-2xl font-bold font-heading text-foreground-950">
                {totalRows.toLocaleString('fr-FR')}
              </div>
              <div className="text-xs text-foreground-500">Lignes (approx.)</div>
            </div>
          </div>

          {identity && (
            <div className="space-y-6">
              <ExportPanel identity={identity} tables={tables} />
              <FunctionsPanel />
              <TableExplorer identity={identity} tables={tables} />
            </div>
          )}
        </>
      )}
    </div>
  );
}